import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { formatVND, formatCompact } from '@/lib/utils';
import { adjustBalance, addSource, updateSource, deleteSource } from '../../../services/sourceService';
import { transferMoney } from '../../../services/transferService';
import { payCreditCard } from '../../../services/creditPaymentService';
import { useData } from '../../store/data';
import { FormSheet, Field, MoneyField, WalletSelect } from '../../components/FormSheet';
import { confirmAsk } from '../../components/confirm';
import { parseMoneyOrNull, parseAmount } from '../entry/formSchema';
import { useAssetsUI, useAction, uid } from './useAssetsUI';
import { walletRefCount } from './walletUsage';

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const useSources = () => useData(s => s.data.sources).filter(s => s.status !== 'inactive');

// ───────── Điều chỉnh số dư (đối soát) ─────────
const REASONS = ['Đối soát sao kê ngân hàng', 'Quên ghi giao dịch', 'Định giá lại tài sản', 'Sửa số dư đầu kỳ'];

export function AdjustSheet({ id }: { id: string }) {
  const close = useAssetsUI(s => s.close);
  const src = useData(s => s.data.sources).find(s => s.id === id);
  const credit = src?.type === 'credit';
  const current = credit ? Number(src?.usedCredit) || 0 : Number(src?.balance) || 0;
  const [value, setValue] = useState(String(current));
  const [reason, setReason] = useState('');
  const { busy, error, exec } = useAction(close);
  const [fieldErr, setFieldErr] = useState<{ value?: string; reason?: string }>({});
  const target = parseMoneyOrNull(value);
  const diff = target === null ? null : target - current;

  const submit = () => {
    const e: typeof fieldErr = {};
    if (target === null) e.value = 'Nhập số hợp lệ (có thể là 0).';
    if (!reason.trim()) e.reason = 'Cần nhập lý do — để lại vết kiểm toán.';
    setFieldErr(e);
    if (e.value || e.reason) return;
    exec(() => adjustBalance(uid(), id, target!, reason), `Đã điều chỉnh ${src?.name}`);
  };

  return (
    <FormSheet open onClose={close} title={`Điều chỉnh ${credit ? 'dư nợ' : 'số dư'} — ${src?.name ?? ''}`}
      description="Đường duy nhất để sửa tay số dư. Luôn lưu số cũ, số mới và lý do." onSubmit={submit} submitLabel="Lưu điều chỉnh" busy={busy} error={error}>
      <p className="num rounded-md bg-muted px-3 py-2 text-sm">Hiện đang ghi: <b>{formatVND(current)}</b></p>
      <MoneyField label={credit ? 'Dư nợ thực tế' : 'Số dư thực tế'} value={value} onChange={setValue} allowZero error={fieldErr.value}
        hint={diff !== null && diff !== 0 ? `Chênh ${diff > 0 ? '+' : '−'}${formatVND(Math.abs(diff))} so với đang ghi` : undefined} />
      <Field label="Lý do" error={fieldErr.reason}>
        <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="vd: đối soát sao kê tháng 10" aria-invalid={!!fieldErr.reason} />
        <div className="flex flex-wrap gap-1.5 pt-1">
          {REASONS.map(r => <Button key={r} type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setReason(r)}>{r}</Button>)}
        </div>
      </Field>
    </FormSheet>
  );
}

// ───────── Chuyển khoản ─────────
export function TransferSheet() {
  const close = useAssetsUI(s => s.close);
  const wallets = useSources().filter(s => s.type !== 'credit');
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const [amount, setAmount] = useState(''); const [fee, setFee] = useState('');
  const [date, setDate] = useState(today()); const [note, setNote] = useState('');
  const [err, setErr] = useState<Record<string, string>>({});
  const { busy, error, exec } = useAction(close);
  const src = wallets.find(w => w.id === from);
  const total = parseAmount(amount) + (parseMoneyOrNull(fee) ?? 0);

  const submit = () => {
    const e: Record<string, string> = {};
    if (!from) e.from = 'Chọn ví nguồn';
    if (!to) e.to = 'Chọn ví đích';
    if (from && from === to) e.to = 'Ví đích phải khác ví nguồn';
    if (parseAmount(amount) <= 0) e.amount = 'Nhập số tiền lớn hơn 0';
    if (fee.trim() && parseMoneyOrNull(fee) === null) e.fee = 'Phí không hợp lệ';
    if (src && total > (Number(src.balance) || 0)) e.amount = `Số dư ${src.name} chỉ còn ${formatVND(Number(src.balance) || 0)} (gồm phí)`;
    setErr(e);
    if (Object.keys(e).length) return;
    exec(() => transferMoney(uid(), { fromSourceId: from, toSourceId: to, amount: parseAmount(amount), fee: parseMoneyOrNull(fee) ?? 0, transferDate: date, note }), 'Đã chuyển khoản');
  };

  return (
    <FormSheet open onClose={close} title="Chuyển khoản" description="Chuyển giữa ví của bạn — không phải chi tiêu. Phí (nếu có) tự ghi thành chi phí tài chính." onSubmit={submit} submitLabel="Chuyển" busy={busy} error={error}>
      <WalletSelect label="Từ ví" value={from} onChange={setFrom} wallets={wallets} error={err.from} />
      <WalletSelect label="Đến ví" value={to} onChange={setTo} wallets={wallets.filter(w => w.id !== from)} error={err.to} />
      <MoneyField label="Số tiền" value={amount} onChange={setAmount} error={err.amount} />
      <MoneyField label="Phí chuyển (nếu có)" value={fee} onChange={setFee} allowZero error={err.fee} hint="Để trống nếu không mất phí" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Ngày"><Input type="date" value={date} onChange={e => setDate(e.target.value)} className="num" /></Field>
        <Field label="Ghi chú"><Input value={note} onChange={e => setNote(e.target.value)} placeholder="Tuỳ chọn" /></Field>
      </div>
    </FormSheet>
  );
}

// ───────── Trả thẻ tín dụng ─────────
export function PayCardSheet({ id }: { id?: string }) {
  const close = useAssetsUI(s => s.close);
  const all = useSources();
  const cards = all.filter(s => s.type === 'credit');
  const wallets = all.filter(s => s.type !== 'credit');
  const [card, setCard] = useState(id ?? ''); const [from, setFrom] = useState('');
  const [amount, setAmount] = useState(''); const [date, setDate] = useState(today()); const [note, setNote] = useState('');
  const [err, setErr] = useState<Record<string, string>>({});
  const { busy, error, exec } = useAction(close);
  const c = cards.find(x => x.id === card);
  const used = Number(c?.usedCredit) || 0;
  const minPct = Number(c?.minimumPaymentPercent) || 5;
  const src = wallets.find(w => w.id === from);

  const submit = () => {
    const e: Record<string, string> = {};
    if (!card) e.card = 'Chọn thẻ';
    if (!from) e.from = 'Chọn ví trả';
    const a = parseAmount(amount);
    if (a <= 0) e.amount = 'Nhập số tiền lớn hơn 0';
    else if (c && a > used) e.amount = `Dư nợ thẻ chỉ còn ${formatVND(used)}`;
    else if (src && a > (Number(src.balance) || 0)) e.amount = `Số dư ${src.name} chỉ còn ${formatVND(Number(src.balance) || 0)}`;
    setErr(e);
    if (Object.keys(e).length) return;
    exec(() => payCreditCard(uid(), { creditCardId: card, sourceWalletId: from, amount: a, note, date: new Date(date) }), 'Đã trả thẻ');
  };

  return (
    <FormSheet open onClose={close} title="Trả thẻ tín dụng" description="Giảm dư nợ thẻ và trừ ví trả. Không tính là chi tiêu (đã tính lúc quẹt thẻ)." onSubmit={submit} submitLabel="Trả thẻ" busy={busy} error={error}>
      <WalletSelect label="Thẻ" value={card} onChange={setCard} wallets={cards} placeholder="Chọn thẻ" error={err.card} />
      <WalletSelect label="Trả từ ví" value={from} onChange={setFrom} wallets={wallets} error={err.from} />
      <MoneyField label="Số tiền trả" value={amount} onChange={setAmount} error={err.amount} />
      {c && (
        <div className="flex flex-wrap gap-1.5">
          <Button type="button" variant="outline" size="sm" onClick={() => setAmount(String(used))}>Toàn bộ dư nợ · {formatCompact(used)}</Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setAmount(String(Math.round(used * minPct / 100)))}>Tối thiểu {minPct}% · {formatCompact(used * minPct / 100)}</Button>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Ngày"><Input type="date" value={date} onChange={e => setDate(e.target.value)} className="num" /></Field>
        <Field label="Ghi chú"><Input value={note} onChange={e => setNote(e.target.value)} placeholder="Tuỳ chọn" /></Field>
      </div>
    </FormSheet>
  );
}

// ───────── Thêm ví / thẻ ─────────
const TYPES = [
  { id: 'cash', label: 'Tiền mặt' }, { id: 'bank', label: 'Ngân hàng' }, { id: 'savings', label: 'Tiết kiệm' },
  { id: 'investment', label: 'Đầu tư' }, { id: 'asset', label: 'Tài sản (vàng, BĐS…)' }, { id: 'credit', label: 'Thẻ tín dụng' }
];

export function AddWalletSheet() {
  const close = useAssetsUI(s => s.close);
  const [name, setName] = useState(''); const [type, setType] = useState('bank');
  const [initial, setInitial] = useState(''); const [limit, setLimit] = useState('');
  const [dueDay, setDueDay] = useState(''); const [minPct, setMinPct] = useState('5');
  const [err, setErr] = useState<Record<string, string>>({});
  const { busy, error, exec } = useAction(close);
  const credit = type === 'credit';
  useEffect(() => setErr({}), [type]);

  const submit = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Nhập tên ví';
    if (credit && parseAmount(limit) <= 0) e.limit = 'Nhập hạn mức lớn hơn 0';
    if (!credit && initial.trim() && parseMoneyOrNull(initial) === null) e.initial = 'Số dư không hợp lệ';
    if (credit && dueDay.trim() && !(Number(dueDay) >= 1 && Number(dueDay) <= 31)) e.dueDay = 'Ngày từ 1 đến 31';
    setErr(e);
    if (Object.keys(e).length) return;
    const data: Record<string, any> = { name: name.trim(), type, status: 'active', isEmergencyFund: false };
    if (credit) Object.assign(data, { creditLimit: parseAmount(limit), usedCredit: 0, paymentDueDay: Number(dueDay) || undefined, minimumPaymentPercent: Number(minPct) || 5 });
    else data.initialBalance = parseMoneyOrNull(initial) ?? 0;
    exec(() => addSource(uid(), data), `Đã thêm ${name.trim()}`);
  };

  return (
    <FormSheet open onClose={close} title="Thêm ví / thẻ" onSubmit={submit} submitLabel="Thêm" busy={busy} error={error}>
      <Field label="Tên" error={err.name}><Input value={name} onChange={e => setName(e.target.value)} placeholder="vd: Techcombank" aria-invalid={!!err.name} /></Field>
      <Field label="Loại">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      {credit ? (
        <>
          <MoneyField label="Hạn mức" value={limit} onChange={setLimit} error={err.limit} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ngày đến hạn trả" error={err.dueDay}><Input value={dueDay} onChange={e => setDueDay(e.target.value)} inputMode="numeric" placeholder="vd: 15" /></Field>
            <Field label="Tối thiểu (%)"><Input value={minPct} onChange={e => setMinPct(e.target.value)} inputMode="numeric" /></Field>
          </div>
        </>
      ) : (
        <MoneyField label="Số dư hiện tại" value={initial} onChange={setInitial} allowZero error={err.initial} hint="Để trống = 0. Sau này chỉ đổi qua giao dịch hoặc Điều chỉnh số dư." />
      )}
    </FormSheet>
  );
}

// ───────── Sửa / ngừng dùng / xoá ví ─────────
export function EditWalletSheet({ id, debtPayments }: { id: string; debtPayments: any[] }) {
  const close = useAssetsUI(s => s.close);
  const all = useData(s => s.data);
  const w = all.sources.find(s => s.id === id);
  const credit = w?.type === 'credit';
  const [name, setName] = useState(w?.name ?? '');
  const [limit, setLimit] = useState(String(w?.creditLimit ?? ''));
  const [dueDay, setDueDay] = useState(String(w?.paymentDueDay ?? ''));
  const [minPct, setMinPct] = useState(String(w?.minimumPaymentPercent ?? 5));
  const [cardRate, setCardRate] = useState(w?.interestRate != null ? String(w.interestRate) : '');
  const [err, setErr] = useState<Record<string, string>>({});
  const { busy, error, exec } = useAction(close);
  const refs = walletRefCount(id, all as any, debtPayments);
  const inactive = w?.status === 'inactive';
  const balanceNonZero = credit ? (Number(w?.usedCredit) || 0) !== 0 : (Number(w?.balance) || 0) !== 0;

  const save = (extra: Record<string, any> = {}, msg = 'Đã cập nhật') => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Nhập tên ví';
    if (credit && cardRate.trim() && !(Number(cardRate) >= 0 && Number(cardRate) <= 100)) e.cardRate = 'Lãi suất từ 0 đến 100 %/năm';
    if (credit && parseAmount(limit) <= 0) e.limit = 'Hạn mức phải lớn hơn 0';
    if (credit && parseAmount(limit) < (Number(w?.usedCredit) || 0)) e.limit = `Hạn mức không được thấp hơn dư nợ hiện tại (${formatVND(Number(w?.usedCredit) || 0)})`;
    setErr(e);
    if (Object.keys(e).length) return;
    const patch: Record<string, any> = { name: name.trim(), ...extra };
    if (credit) Object.assign(patch, { creditLimit: parseAmount(limit), paymentDueDay: Number(dueDay) || null, minimumPaymentPercent: Number(minPct) || 5, ...(cardRate.trim() ? { interestRate: Number(cardRate) } : w?.interestRate != null ? { interestRate: null } : {}) });
    exec(() => updateSource(uid(), id, patch), msg);
  };
  const remove = async () => {
    if (!await confirmAsk({ title: `Xoá vĩnh viễn ví "${w?.name}"?`, description: 'Không thể hoàn tác.', confirmLabel: 'Xoá ví' })) return;
    exec(() => deleteSource(uid(), id), 'Đã xoá ví');
  };

  return (
    <FormSheet open onClose={close} title={`Sửa ${credit ? 'thẻ' : 'ví'} — ${w?.name ?? ''}`} onSubmit={() => save()} submitLabel="Lưu" busy={busy} error={error}
      description="Đổi tên không ảnh hưởng giao dịch cũ (liên kết theo mã ví, không theo tên). Số dư chỉ đổi qua giao dịch hoặc Điều chỉnh số dư.">
      <Field label="Tên" error={err.name}><Input value={name} onChange={e => setName(e.target.value)} aria-invalid={!!err.name} /></Field>
      {credit && (
        <>
          <MoneyField label="Hạn mức" value={limit} onChange={setLimit} error={err.limit} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ngày đến hạn trả"><Input value={dueDay} onChange={e => setDueDay(e.target.value)} inputMode="numeric" className="num" /></Field>
            <Field label="Tối thiểu (%)"><Input value={minPct} onChange={e => setMinPct(e.target.value)} inputMode="numeric" className="num" /></Field>
          </div>
          <Field label="Lãi suất khi không trả đủ (%/năm, tuỳ chọn)" error={err.cardRate} hint="Xem trong sao kê hoặc hợp đồng thẻ. Dùng để tính chi phí lãi và xếp thứ tự trả nợ."><Input value={cardRate} onChange={e => setCardRate(e.target.value)} inputMode="decimal" className="num" /></Field>
        </>
      )}
      <div className="space-y-2 border-t border-border pt-4">
        <p className="text-sm font-medium">{inactive ? 'Ví đang ngừng dùng' : 'Ngừng dùng ví này'}</p>
        <p className="text-xs text-muted-foreground">Ví ngừng dùng bị ẩn khỏi mọi danh sách chọn ví nhưng vẫn giữ nguyên lịch sử và số dư.</p>
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => save({ status: inactive ? 'active' : 'inactive' }, inactive ? 'Đã dùng lại ví' : 'Đã ngừng dùng ví')}>{inactive ? 'Dùng lại ví' : 'Ngừng dùng'}</Button>
      </div>
      <div className="space-y-2 border-t border-border pt-4">
        <p className="text-sm font-medium text-bad">Xoá ví</p>
        {refs > 0 ? <p className="text-xs text-muted-foreground">Ví đang được {refs} bản ghi tham chiếu nên không xoá được — hãy chọn "Ngừng dùng".</p>
          : balanceNonZero ? <p className="text-xs text-muted-foreground">Ví còn số dư/dư nợ khác 0 — điều chỉnh về 0 trước khi xoá.</p>
          : <Button type="button" variant="destructive" size="sm" disabled={busy} onClick={remove}>Xoá ví</Button>}
      </div>
    </FormSheet>
  );
}
